import { FormEvent, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { PageHeader } from '../../components/layout/page/PageHeader';
import { PageShell } from '../../components/layout/page/PageShell';
import { CollapsibleFormSection } from '../../components/ui/CollapsibleFormSection';
import { DeleteIconButton } from '../../components/ui/DeleteIconButton';
import { EditIconButton } from '../../components/ui/EditIconButton';
import { FormActionBar } from '../../components/ui/FormActionBar';
import { ListPaginationBar } from '../../components/ui/ListPaginationBar';
import { useConfirmDialog } from '../../contexts/ConfirmDialogContext';
import { usePermissions } from '../../contexts/PermissionsContext';
import { useCollapsibleItemForm } from '../../hooks/useCollapsibleItemForm';
import {
  TechnologyAiPrompt,
  TechnologyAiPromptUpsertPayload,
  TechnologyAiUsage,
  TechnologyCatalogItem,
  createTechnologyAiPrompt,
  deleteTechnologyAiPrompt,
  listTechnologyAiPrompts,
  listTechnologyAiUsages,
  listTechnologyCatalogItems,
  updateTechnologyAiPrompt,
} from '../../services/technologyApi';
import { TECHNOLOGY_AI_USAGE_STATUS_OPTIONS, technologyOptionLabel } from './technologyFormOptions';

const PAGE_SIZE = 10;

const emptyForm: TechnologyAiPromptUpsertPayload = {
  name: '',
  usageId: '',
  catalogItemId: '',
  fallbackCatalogItemId: '',
  body: '',
  temperature: null,
  datasetNotes: '',
  status: 'Active',
};

function readProblemDetail(error: unknown): string | null {
  if (typeof error !== 'object' || error == null || !('response' in error)) {
    return null;
  }

  const response = (error as { response?: { data?: { detail?: string } } }).response;
  return response?.data?.detail ?? null;
}

function optionalNumber(value: string): number | null {
  if (!value.trim()) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

export function TechnologyAiPromptsPage() {
  const { canWriteModule } = usePermissions();
  const { confirm } = useConfirmDialog();
  const canWrite = canWriteModule('technology.ai-prompts');
  const [items, setItems] = useState<TechnologyAiPrompt[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [versionLabel, setVersionLabel] = useState(1);
  const [temperatureText, setTemperatureText] = useState('');
  const [form, setForm] = useState<TechnologyAiPromptUpsertPayload>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [usages, setUsages] = useState<TechnologyAiUsage[]>([]);
  const [models, setModels] = useState<TechnologyCatalogItem[]>([]);
  const { formExpanded, expandForm, collapseForm, handleCreateSuccess, toggleOrCancel } =
    useCollapsibleItemForm();
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  async function loadData(targetPage = page): Promise<void> {
    setLoading(true);
    try {
      const result = await listTechnologyAiPrompts({
        page: targetPage,
        pageSize: PAGE_SIZE,
        search: search.trim() || undefined,
      });
      setItems(result.items);
      setTotalCount(result.totalCount);
      setPage(result.page);
    } catch (error) {
      toast.error(readProblemDetail(error) ?? 'Falha ao carregar prompts.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadData(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  useEffect(() => {
    void listTechnologyAiUsages({ page: 1, pageSize: 200 })
      .then((result) => setUsages(result.items))
      .catch(() => setUsages([]));
    void listTechnologyCatalogItems({ page: 1, pageSize: 200, category: 'AiModel' })
      .then((result) => setModels(result.items))
      .catch(() => setModels([]));
  }, []);

  function resetForm(): void {
    setEditingId(null);
    setVersionLabel(1);
    setTemperatureText('');
    setForm(emptyForm);
  }

  function startEdit(item: TechnologyAiPrompt): void {
    expandForm();
    setEditingId(item.id);
    setVersionLabel(item.version);
    setTemperatureText(item.temperature == null ? '' : String(item.temperature));
    setForm({
      name: item.name,
      usageId: item.usageId,
      catalogItemId: item.catalogItemId ?? '',
      fallbackCatalogItemId: item.fallbackCatalogItemId ?? '',
      body: item.body,
      temperature: item.temperature ?? null,
      datasetNotes: item.datasetNotes ?? '',
      status: item.status,
    });
  }

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!canWrite) return;

    const payload: TechnologyAiPromptUpsertPayload = {
      ...form,
      catalogItemId: form.catalogItemId || null,
      fallbackCatalogItemId: form.fallbackCatalogItemId || null,
      temperature: optionalNumber(temperatureText),
      datasetNotes: form.datasetNotes?.trim() || null,
    };

    setSaving(true);
    try {
      if (editingId) {
        const updated = await updateTechnologyAiPrompt(editingId, payload);
        toast.success(`Prompt atualizado. Versão ${updated.version}.`);
        collapseForm(resetForm);
      } else {
        await createTechnologyAiPrompt(payload);
        toast.success('Prompt criado.');
        handleCreateSuccess(resetForm);
      }
      await loadData(page);
    } catch (error) {
      toast.error(readProblemDetail(error) ?? 'Falha ao salvar prompt.');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(item: TechnologyAiPrompt): Promise<void> {
    if (!canWrite) return;
    const accepted = await confirm({
      title: 'Excluir prompt',
      message: `Deseja excluir "${item.name}"?`,
      confirmLabel: 'Excluir',
      tone: 'danger',
    });
    if (!accepted) return;

    try {
      await deleteTechnologyAiPrompt(item.id);
      toast.success('Prompt excluído.');
      if (editingId === item.id) collapseForm(resetForm);
      await loadData(page);
    } catch (error) {
      toast.error(readProblemDetail(error) ?? 'Falha ao excluir prompt.');
    }
  }

  return (
    <PageShell>
      <PageHeader
        title="Prompts"
        description="Texto, temperatura, modelo e fallback de cada uso de IA. A versão sobe quando o conteúdo muda."
      />

      {canWrite ? (
        <CollapsibleFormSection
          title={editingId ? 'Editar prompt' : 'Novo prompt'}
          expanded={formExpanded}
          onToggle={() => toggleOrCancel(resetForm)}
          editingSummary={
            editingId ? `Editando ${form.name || 'prompt'} - versão ${versionLabel}` : undefined
          }
          createSummary="Clique para expandir e cadastrar um prompt"
          isEditing={!!editingId}
        >
          <form onSubmit={(event) => void handleSubmit(event)} className="erp-form px-4 pb-4 pt-3">
            <div className="erp-form-grid">
              <label className="erp-label">
                Nome
                <input
                  className="erp-input"
                  value={form.name}
                  onChange={(event) => setForm({ ...form, name: event.target.value })}
                  required
                />
              </label>
              <label className="erp-label">
                Uso de IA
                <select
                  className="erp-input"
                  value={form.usageId}
                  onChange={(event) => setForm({ ...form, usageId: event.target.value })}
                  required
                >
                  <option value="">Selecione</option>
                  {usages.map((usage) => (
                    <option key={usage.id} value={usage.id}>
                      {usage.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="erp-label">
                Modelo
                <select
                  className="erp-input"
                  value={form.catalogItemId ?? ''}
                  onChange={(event) => setForm({ ...form, catalogItemId: event.target.value })}
                >
                  <option value="">Nenhum</option>
                  {models.map((model) => (
                    <option key={model.id} value={model.id}>
                      {model.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="erp-label">
                Fallback
                <select
                  className="erp-input"
                  value={form.fallbackCatalogItemId ?? ''}
                  onChange={(event) =>
                    setForm({ ...form, fallbackCatalogItemId: event.target.value })
                  }
                >
                  <option value="">Nenhum</option>
                  {models.map((model) => (
                    <option key={model.id} value={model.id}>
                      {model.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="erp-label">
                Temperatura
                <input
                  className="erp-input"
                  inputMode="decimal"
                  value={temperatureText}
                  onChange={(event) => setTemperatureText(event.target.value)}
                  placeholder="0 a 2"
                />
              </label>
              <label className="erp-label">
                Status
                <select
                  className="erp-input"
                  value={form.status}
                  onChange={(event) => setForm({ ...form, status: event.target.value })}
                >
                  {TECHNOLOGY_AI_USAGE_STATUS_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <label className="erp-label mt-3 block">
              Texto do prompt
              <textarea
                className="erp-input mt-1 min-h-[120px]"
                value={form.body}
                onChange={(event) => setForm({ ...form, body: event.target.value })}
                required
              />
            </label>
            <label className="erp-label mt-3 block">
              Dataset relacionado
              <textarea
                className="erp-input mt-1 min-h-[60px]"
                value={form.datasetNotes ?? ''}
                onChange={(event) => setForm({ ...form, datasetNotes: event.target.value })}
              />
            </label>
            <FormActionBar
              onCancel={() => collapseForm(resetForm)}
              cancelLabel="Cancelar"
              className="erp-btn-actions mt-4"
            >
              <button type="submit" disabled={saving} className="erp-btn-primary">
                {saving ? 'Salvando...' : editingId ? 'Atualizar' : 'Criar'}
              </button>
            </FormActionBar>
          </form>
        </CollapsibleFormSection>
      ) : null}

      <div className="mb-3 flex flex-wrap gap-2">
        <input
          className="erp-input max-w-xs"
          placeholder="Buscar por nome, texto ou uso"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              setPage(1);
              void loadData(1);
            }
          }}
        />
        <button
          type="button"
          className="erp-btn-secondary"
          onClick={() => {
            setPage(1);
            void loadData(1);
          }}
        >
          Buscar
        </button>
      </div>

      {loading ? (
        <p className="text-secondary-500">Carregando...</p>
      ) : (
        <>
          <div className="overflow-x-auto rounded-xl border border-secondary-200 bg-white">
            <table className="erp-table">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Uso</th>
                  <th>Modelo</th>
                  <th>Versão</th>
                  <th>Status</th>
                  {canWrite ? <th className="w-24">Ações</th> : null}
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr>
                    <td colSpan={canWrite ? 6 : 5} className="text-center text-secondary-500">
                      Nenhum prompt cadastrado.
                    </td>
                  </tr>
                ) : (
                  items.map((item) => (
                    <tr key={item.id}>
                      <td>{item.name}</td>
                      <td>{item.usageName || '—'}</td>
                      <td>{item.catalogItemName || '—'}</td>
                      <td>{item.version}</td>
                      <td>
                        {technologyOptionLabel(TECHNOLOGY_AI_USAGE_STATUS_OPTIONS, item.status)}
                      </td>
                      {canWrite ? (
                        <td>
                          <div className="flex gap-1">
                            <EditIconButton onClick={() => startEdit(item)} label="Editar" />
                            <DeleteIconButton
                              onClick={() => void handleDelete(item)}
                              label="Excluir"
                            />
                          </div>
                        </td>
                      ) : null}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {totalCount > PAGE_SIZE ? (
            <ListPaginationBar
              totalCount={totalCount}
              page={page}
              pageSize={PAGE_SIZE}
              onPrevious={() => setPage((current) => Math.max(1, current - 1))}
              onNext={() => setPage((current) => Math.min(totalPages, current + 1))}
            />
          ) : null}
        </>
      )}
    </PageShell>
  );
}
